<script>
import RelationInput from "@/components/Forms/RelationInput.vue";
import QuerySelect from "@/components/Forms/QuerySelect.vue";
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import AvailabilityStatus from "@/components/Loanable/AvailabilityStatus.vue";
import FormSection from "@/components/Loanable/FormSection.vue";
import LoanableUserRoleForm from "@/components/Loanable/UserRoleForm.vue";
import ConditionalContext from "@/components/shared/ConditionalContext.vue";
import FormButtons from "@/components/shared/FormButtons.vue";
import IconButton from "@/components/shared/IconButton.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column } from "@/components/shared/PaginatedTableColumns";
import ValidatedButton from "@/components/shared/ValidatedButton.vue";
import { capitalize } from "@/helpers/filters";
import { communityLink, loanableLink, userLink } from "@/helpers/links";
import { getOwner, hasSomeLibraryRole } from "@/helpers/permissions/loanables";
import { canSeeDeletedUser, isGlobalAdmin } from "@/helpers/permissions/users";
import {
  addTrustedBorrower,
  searchUsersAssignableToRole,
} from "@/requests/libraryUserRolesRequests";
import { removeTrustedBorrower } from "@/requests/loanableUserRolesRequests";
import { del, get, post, put } from "@/requests/server";

export default {
  name: "LibraryForm",
  components: {
    AvailabilityStatus,
    ConditionalContext,
    FormSection,
    LoanableUserRoleForm,
    QuerySelect,
    RelationInput,
    IconButton,
    FormButtons,
    PaginatedTable,
    ValidatedButton,
    FormsValidatedInput,
  },
  props: {
    id: {
      type: [String, Number],
      required: false,
      default: null,
    },
  },
  data() {
    return {
      library: {},
      loading: false,
      initialLibrary: null,
      loaded: !this.id || this.id === "new", // if no id, we are loaded
      editedRole: {
        user: null,
        role: null,
      },
      userRolesColumns: [
        Column.withRelation(
          "user.full_name",
          capitalize(this.$t("users.fields.user")),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: {
              "library_roles.ressource_id": this.id,
            },
          },
          "user_id",
          {
            urlFct: (userRole) => userLink(userRole.user),
          }
        ),
        Column.withSelect("role", this.$t("loanables.fields.user_role"), [
          { value: null, label: this.$t("loanables.roles.all") },
          { value: "owner", label: this.$t("loanables.roles.owner") },
          { value: "manager", label: this.$t("loanables.roles.manager") },
        ]),
        Column.withRelation(
          "granted_by_user.full_name",
          this.$t("user_roles.fields.granted_by"),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: {
              for: "profile",
            },
          },
          "granted_by_user_id",
          {
            urlFct: (userRole) => userLink(userRole.granted_by_user),
          }
        ),
        new Column(
          "pays_loan_price",
          this.$t("components.loanable.user_roles_list.pays_price"),
          "boolean"
        ),
        new Column(
          "pays_loan_insurance",
          this.$t("components.loanable.user_roles_list.pays_insurance"),
          "boolean"
        ),
        new Column(
          "show_as_contact",
          this.$t("components.loanable.user_roles_list.contact"),
          "boolean"
        ),
      ],
      communitiesColumns: [
        Column.withRelation(
          "name",
          capitalize(this.$tc("communities.community", 1)),
          {
            relation: "communities",
            label: "name",
            field: "id",
            params: { for: "profile", "libraries.id": this.id },
          },
          "id",
          {
            urlFct: communityLink,
          }
        ),
      ],
      loanablesColumns: [
        Column.withRelation(
          "name",
          capitalize(this.$t("loanables.fields.name")),
          {
            relation: "loanables",
            label: "name",
            field: "id",
            params: { for: "profile", "library.id": this.id },
          },
          "id",
          { urlFct: (loanable) => `/loanables/${loanable.id}` }
        ),
        Column.withSelect("type", capitalize(this.$t("loanables.fields.type")), [
          { value: null, label: capitalize(this.$t("loanables.fields.types.all")) },
          { value: "car", label: capitalize(this.$t("loanables.fields.types.car")) },
          { value: "trailer", label: capitalize(this.$t("loanables.fields.types.trailer")) },
          { value: "bike", label: capitalize(this.$t("loanables.fields.types.bike")) },
        ]),
        Column.withSelect(
          "availability_status",
          capitalize(this.$t("loanables.fields.availability_status.title")),
          [
            {
              value: null,
              label: capitalize(this.$t("loanables.fields.availability_status.options.null.all")),
            },
            {
              value: "no_approved_communities",
              label: capitalize(
                this.$t("loanables.fields.availability_status.options.no_approved_communities")
              ),
              variant: "warning",
            },
            {
              value: "wrong_type",
              label: capitalize(this.$t("loanables.fields.availability_status.options.wrong_type")),
              variant: "danger",
            },
            {
              value: "no_availabilities",
              label: capitalize(
                this.$t("loanables.fields.availability_status.options.no_availabilities")
              ),
              variant: "warning",
            },
            {
              value: "unpublished",
              label: capitalize(
                this.$t("loanables.fields.availability_status.options.unpublished")
              ),
              variant: "secondary",
            },
            {
              value: "has_availabilities",
              label: capitalize(
                this.$t("loanables.fields.availability_status.options.has_availabilities")
              ),
              variant: "success",
            },
          ]
        ),
      ],
    };
  },
  computed: {
    isNew() {
      return !this.library.id && (!this.id || this.id === "new");
    },
    user() {
      return this.$store.state.user;
    },
    isGlobalAdmin() {
      return isGlobalAdmin(this.user);
    },
    libraryOwner() {
      return this.library.owner_user;
    },
    isLibraryOwner() {
      return this.libraryOwner.id === this.user.id;
    },
    canManageCommunities() {
      return this.isGlobalAdmin || this.isLibraryOwner;
    },
    canManageRoles() {
      return this.isGlobalAdmin || this.isLibraryOwner;
    },
    canManageLoanables() {
      return this.isGlobalAdmin || this.isLibraryOwner;
    },
    canEditRole() {
      // Not allowing editing self role, since there are no additional options to edit currently.
      // We allow removing self role (see method canRemoveRole).
      return this.isGlobalAdmin || this.isLibraryOwner;
    },
    trustedBorrowersColumns() {
      let trustedBorrowersColumns = [
        new Column("user.full_name", capitalize(this.$t("users.fields.full_name"))),
        Column.withRelation(
          "granted_by_user.full_name",
          capitalize(this.$t("user_roles.fields.granted_by")),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            details: "email",
            params: { with_deleted: canSeeDeletedUser(this.user) },
          },
          "granted_by_user_id",
          { showByDefault: false }
        ),
      ];

      if (
        this.library &&
        (isGlobalAdmin(this.user) ||
          hasSomeLibraryRole(this.user, this.library, ["owner", "manager"]))
      ) {
        trustedBorrowersColumns.push(
          new Column("user.phone", capitalize(this.$t("users.fields.phone")))
        );
        trustedBorrowersColumns.push(
          new Column("user.email", capitalize(this.$t("users.fields.email")))
        );
      }

      return trustedBorrowersColumns;
    },
    assignableUsersToRoleSearchFct() {
      const libraryId = this.id;
      const role = "trusted_borrower";

      let params = {
        per_page: 10,
      };

      return async function (q) {
        const data = await searchUsersAssignableToRole(libraryId, role, q, params);

        // Create options from returned data.
        return data.map((item) => {
          return {
            value: item.id,
            label: item.full_name,
            details: item.email,
            object: item,
          };
        });
      };
    },
    changed() {
      return JSON.stringify(this.library) !== this.initialLibrary;
    },
  },
  mounted() {
    if (!this.loaded) {
      this.loadLibrary();
    }
  },
  methods: {
    loanableLink,
    communityLink,
    capitalize,
    canRemoveRole(role) {
      if (role.role === "owner") {
        // Must always have an owner.
        return false;
      }
      return this.isGlobalAdmin || this.isLibraryOwner || role.user.id === this.user.id;
    },
    async loadLibrary() {
      this.loading = true;
      const { data } = await get(`/libraries/${this.id}`, {
        notifications: { action: this.$t("components.library.form.notifications.loading") },
        cleanupCallback: () => (this.loading = false),
      });
      this.loaded = true;
      this.library = data;
      this.initialLibrary = JSON.stringify(data);
    },
    async onSelectTrustedBorrower(option) {
      if (!this.id) {
        return;
      }

      if (option) {
        const user = option?.object;

        if (user?.id) {
          await addTrustedBorrower(this.id, user.id);

          this.$refs.trustedBorrowerSelect.reset();
          this.$refs.trustedBorrowerTable.refresh();
        }
      }
    },
    async removeTrustedBorrower(trustedBorrower) {
      await removeTrustedBorrower(trustedBorrower);
      this.$refs.trustedBorrowerTable.refresh();
    },
    addRole() {
      this.editedRole = {
        id: null,
        user: null,
        role: "manager",
        pays_loan_price: true,
        pays_loan_insurance: true,
      };
      this.$refs.editRoleModal.show();
    },
    editRole(userRole) {
      this.editedRole = {
        ...userRole,
      };

      this.$refs.editRoleModal.show();
    },
    async removeUserRole(userRole) {
      await del(`/loanables/roles/${userRole.id}`, {
        notifications: {
          action: this.$t("components.library.form.notifications.delete_user_role"),
        },
      });
      if (userRole.user.id === this.user.id) {
        this.$router.push("/");
        return;
      }
      this.$refs.userRolesTable.refresh();
    },
    async save() {
      if (this.loading) {
        return;
      }
      this.loading = true;
      if (this.isNew) {
        const { data } = await post("/libraries", this.library, {
          notifications: {
            action: this.$t("components.library.form.notifications.save"),
            onSuccess: true,
          },
          cleanupCallback: () => (this.loading = false),
        });
        this.library = data;
        this.editedRole.user = null;
        this.$router.replace(this.$route.fullPath.replace("new", data.id));
      } else {
        const { data } = await put("/libraries/" + this.id, this.library, {
          notifications: {
            action: this.$t("components.library.form.notifications.save"),
            onSuccess: true,
          },
          cleanupCallback: () => (this.loading = false),
        });
        this.library = data;
        this.initialLibrary = JSON.stringify(data);
      }
      this.$refs.observer.reset();
    },
    async addCommunity(community) {
      const { data } = await post(
        `/libraries/${this.id}/communities/${community.id}`,
        {},
        {
          notifications: {
            action: this.$t("components.library.form.notifications.add_community"),
            onSuccess: true,
          },
        }
      );
      this.library = data;
      this.$refs.communitiesTable.refresh();
    },
    async removeCommunity(community) {
      const { data } = await del(`/libraries/${this.id}/communities/${community.id}`, {
        notifications: {
          action: this.$t("components.library.form.notifications.remove_community"),
          onSuccess: true,
        },
      });
      this.library = data;
      this.$refs.communitiesTable.refresh();
    },
    async addLoanable(loanable) {
      if (!loanable) {
        return;
      }
      const loanableOwnerUser = getOwner(loanable);

      if (loanableOwnerUser.id !== this.libraryOwner.id) {
        const confirm = await this.$bvModal.msgBoxConfirm(
          this.$t("components.library.form.confirmations.change_owner", {
            name: loanable.name,
            old_owner: loanableOwnerUser.full_name,
            new_owner: this.libraryOwner.full_name,
          })
        );
        if (!confirm) {
          return;
        }
      }

      const { data } = await post(
        `/libraries/${this.id}/loanables/${loanable.id}`,
        {},
        {
          notifications: {
            action: this.$t("components.library.form.notifications.add_loanable"),
            onSuccess: true,
          },
        }
      );
      this.library = data;
      this.$refs.loanableInput.clearSearch();
      this.$refs.loanablesTable.refresh();
    },
    async removeLoanable(loanable) {
      const { data } = await del(`/libraries/${this.id}/loanables/${loanable.id}`, {
        notifications: {
          action: this.$t("components.library.form.notifications.remove_loanable"),
          onSuccess: true,
        },
      });
      this.library = data;
      this.$refs.loanablesTable.refresh();
    },
  },
};
</script>

<template>
  <layout-loading v-if="!loaded && loading" />
  <div v-else-if="loaded" class="library-form">
    <h1 v-if="library.id">{{ library.name }}</h1>
    <h1 v-else class="font-italic">{{ $t("components.library.form.title.new") }}</h1>

    <validation-observer ref="observer" v-slot="{ passes }">
      <b-form>
        <form-section>
          <b-row>
            <b-col md="8">
              <forms-validated-input
                v-model="library.name"
                name="name"
                :rules="{ required: true }"
                :label="$t('libraries.fields.name') | capitalize"
                type="text"
              />
              <forms-validated-input
                v-model="library.phone_number"
                name="phone_number"
                :label="$t('libraries.fields.phone_number') | capitalize"
                type="text"
                mask="(###) ###-####"
              />
              <forms-validated-input
                v-if="isNew"
                type="relation"
                name="owner_user_id"
                :query="{
                  slug: 'users',
                  value: 'id',
                  text: 'full_name',
                  details: 'email',
                }"
                :value="library.owner_user_id"
                :object-value="editedRole.user"
                :rules="{ required: true }"
                :label="$t('libraries.fields.owner_user_id') | capitalize"
                mode="eager"
                @relation="
                  editedRole.user = $event;
                  library.owner_user_id = editedRole.user.id;
                "
              />
            </b-col>
            <b-col md="4">
              <forms-validated-input
                v-model="library.avatar"
                type="image"
                name="avatar"
                :label="capitalize($t('libraries.fields.avatar'))"
                preview-aspect-ratio="1 / 1"
              >
              </forms-validated-input>
            </b-col>
          </b-row>
        </form-section>

        <template v-if="!isNew">
          <form-section
            :section-title="$t('components.library.form.sections.loan_configuration')"
            toggleable
            :inititally-visible="false"
          >
            <conditional-context
              show
              :label="$t('components.library.form.auto_validation.title')"
              class="m-0"
            >
              <p>
                {{ $t("components.library.form.auto_validation.description") }}
              </p>
              <div v-if="library.loan_auto_validation_question === null" class="mb-3">
                <icon-button role="add" @click="library.loan_auto_validation_question = ''">
                  {{ $t("components.library.form.auto_validation.add") }}
                </icon-button>
              </div>
              <template v-else>
                <forms-validated-input
                  v-model="library.loan_auto_validation_question"
                  type="markdown"
                  name="loan_auto_validation_question"
                  :label="$t('components.library.form.auto_validation.question.label')"
                  :placeholder="$t('components.library.form.auto_validation.question.placeholder')"
                  :show-preview-initially="!!library.loan_auto_validation_question"
                ></forms-validated-input>
                <forms-validated-input
                  v-model="library.loan_auto_validation_answer"
                  type="text"
                  name="loan_auto_validation_answer"
                  :label="$t('components.library.form.auto_validation.answer.label')"
                ></forms-validated-input>

                <icon-button
                  role="delete"
                  class="mb-3"
                  @click="
                    () => {
                      library.loan_auto_validation_question = null;
                      library.loan_auto_validation_answer = null;
                    }
                  "
                >
                  {{ $t("components.library.form.auto_validation.remove") }}
                </icon-button>
              </template>
            </conditional-context>
          </form-section>

          <form-section
            toggleable
            :section-title="$t('components.library.form.tables.roles.title')"
          >
            <paginated-table
              ref="userRolesTable"
              data-dusk="roles-table"
              :endpoint="`/libraries/${library.id}/roles`"
              :columns="userRolesColumns"
              :show-generate-csv="false"
              :fetch-params="{
                '!role': 'trusted_borrower',
              }"
            >
              <template #head-buttons>
                <icon-button
                  v-if="canManageRoles"
                  role="add"
                  data-dusk="add-role-button"
                  @click="addRole"
                  >{{ $t("components.library.form.tables.roles.add_role") }}</icon-button
                >
              </template>
              <template #cell(actions)="{ item }">
                <icon-button v-if="canEditRole" role="edit" size="sm" @click="() => editRole(item)">
                </icon-button>
                <validated-button
                  v-if="canRemoveRole(item)"
                  :action="async () => removeUserRole(item)"
                  :question="
                    $t('components.library.form.tables.roles.remove_user', {
                      name: item.user.full_name,
                    })
                  "
                  :label="$t('components.library.form.tables.roles.remove')"
                  icon="trash"
                  size="sm"
                  variant="ghost-danger"
                  :show-button-label="false"
                />
              </template>
            </paginated-table>

            <loanable-user-role-form
              ref="editRoleModal"
              :role="editedRole"
              :library="library"
              @saved="$refs.userRolesTable.refresh()"
            />
          </form-section>

          <form-section
            toggleable
            :section-title="$t('components.library.form.tables.communities.title')"
          >
            <paginated-table
              ref="communitiesTable"
              data-dusk="communities-table"
              :endpoint="`/libraries/${library.id}/communities`"
              :columns="communitiesColumns"
              :show-generate-csv="false"
              @row-clicked="
                (item) => (communityLink(item) ? $router.push(communityLink(item)) : null)
              "
            >
              <template #head-buttons>
                <relation-input
                  v-if="canManageCommunities"
                  :placeholder="$t('components.library.form.tables.communities.add')"
                  :value="null"
                  reset-after-select
                  :min-search-length="0"
                  :query="{
                    slug: 'communities',
                    value: 'id',
                    text: 'name',
                    params: {
                      for: 'profile',
                      '!libraries.id': library.id,
                    },
                  }"
                  name="add-community"
                  data-dusk="add-community-input"
                  @input="addCommunity"
                />
              </template>
              <template #cell(actions)="{ item }">
                <validated-button
                  v-if="canManageCommunities"
                  :action="async () => removeCommunity(item)"
                  :question="
                    $t('components.library.form.tables.communities.remove', { name: item.name })
                  "
                  :label="$t('components.library.form.tables.roles.remove')"
                  icon="trash"
                  variant="ghost-danger"
                  size="sm"
                  :show-button-label="false"
                />
                <div v-else></div>
              </template>
            </paginated-table>
          </form-section>

          <form-section toggleable :section-title="$t('components.loanable.form.trusted_network')">
            <b-alert variant="info" show>
              {{ $t("components.library.form.trusted_network.remark") }}
            </b-alert>

            <paginated-table
              ref="trustedBorrowerTable"
              :endpoint="`/libraries/${library.id}/roles`"
              :columns="trustedBorrowersColumns"
              :show-generate-csv="false"
              :load-relations="['user']"
              :fetch-params="{
                role: 'trusted_borrower',
              }"
            >
              <template #head-buttons>
                <query-select
                  ref="trustedBorrowerSelect"
                  :placeholder="$t('components.library.form.add_member')"
                  :search-fct="assignableUsersToRoleSearchFct"
                  class="add-user-input"
                  name="new_trusted_member"
                  @input="onSelectTrustedBorrower"
                />
              </template>

              <template #cell(actions)="{ item }">
                <validated-button
                  :action="async () => removeTrustedBorrower(item)"
                  :question="
                    $t('components.loanable.form.remove_member', { name: item.user.full_name })
                  "
                  :label="capitalize($t('forms.remove'))"
                  icon="trash"
                  :show-button-label="false"
                  size="sm"
                  variant="ghost-danger"
                />
              </template>
            </paginated-table>
          </form-section>

          <form-section
            toggleable
            :section-title="$t('components.library.form.tables.loanables.title') | capitalize"
          >
            <paginated-table
              ref="loanablesTable"
              data-dusk="loanables-table"
              :endpoint="`/libraries/${library.id}/loanables`"
              :columns="loanablesColumns"
              :load-relations="['active_incidents', 'merged_user_roles']"
              :show-generate-csv="false"
              @row-clicked="(item) => $router.push(`/loanables/${item.id}`)"
            >
              <template #head-buttons>
                <relation-input
                  v-if="canManageLoanables"
                  ref="loanableInput"
                  :placeholder="$t('components.library.form.tables.loanables.add')"
                  :value="null"
                  reset-after-select
                  :min-search-length="0"
                  :query="{
                    slug: 'loanables',
                    value: 'id',
                    text: 'name',
                    params: {
                      for: 'owner',
                      '!library.id': library.id,
                      relations: 'merged_user_roles.user',
                    },
                  }"
                  name="add-loanable"
                  data-dusk="add-loanable-input"
                  @input="addLoanable"
                />
              </template>
              <template #cell(availability_status)="{ item }">
                <availability-status :loanable="item" />
              </template>
              <template #cell(actions)="{ item }">
                <icon-button
                  role="edit"
                  size="sm"
                  :to="loanableLink(item)"
                  data-dusk="edit-loanable-button"
                />
                <validated-button
                  v-if="canManageLoanables"
                  :action="async () => removeLoanable(item)"
                  :question="
                    $t('components.library.form.tables.loanables.remove', { name: item.name })
                  "
                  :label="$t('components.library.form.tables.roles.remove')"
                  icon="trash"
                  variant="ghost-danger"
                  :show-button-label="false"
                  size="sm"
                />

                <div v-else></div>
              </template>
            </paginated-table>
          </form-section>
        </template>

        <form-buttons>
          <icon-button
            role="save"
            :onclick="async () => passes(save)"
            :disabled="loading || !changed"
            >{{ $t("forms.save") | capitalize }}</icon-button
          >
        </form-buttons>
      </b-form>
    </validation-observer>
  </div>
  <div v-else>{{ $t("components.library.form.loading.error") }}</div>
</template>

<style scoped lang="scss">
.library-form {
  .add-user-input {
    min-width: 15rem;
  }
  .forms-relation-input {
    min-width: 15rem;
  }
}
</style>
